import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useUserStore } from './store/userStore';
import { authWithTelegram } from './api/client';
import { parseCompatInvite, parseReferrerId, useTelegram } from './hooks/useTelegram';
import i18n from './i18n';

import Starfield from './components/Starfield';
import Header from './components/Header';

import Onboarding from './screens/Onboarding';
import Home from './screens/Home';
import Reading from './screens/Reading';
import Spreads from './screens/Spreads';
import Dream from './screens/Dream';
import YesNo from './screens/YesNo';
import Weekly from './screens/Weekly';
import NameCompat from './screens/NameCompat';
import Numerology from './screens/Numerology';
import NatalChart from './screens/NatalChart';
import SynastryScreen from './screens/SynastryScreen';
import Demo from './screens/Demo';
import AngelNumber from './screens/AngelNumber';
import Journal from './screens/Journal';
import Themed from './screens/Themed';
import Diary from './screens/Diary';
import Profile from './screens/Profile';
import Compat from './screens/Compat';
import Master from './screens/Master';
import Rituals from './screens/Rituals';
import Admin from './screens/Admin';

import { parentRoute } from './lib/navigation';

export default function App() {
  const { user, needsOnboarding, setUser, setNeedsOnboarding } = useUserStore();
  const { startParam } = useTelegram();
  const navigate = useNavigate();
  const location = useLocation();
  // Auth state machine — used to render a retry screen when the backend
  // is temporarily unreachable (Supabase pool hiccup, transient network
  // glitch). Without this the splash screen would freeze forever and
  // users would assume the app is broken.
  const [authError, setAuthError] = useState<string | null>(null);
  const [authAttempt, setAuthAttempt] = useState(0);

  // Admin dashboard is reachable from a normal desktop browser (no Telegram
  // WebApp context). It has its own token-based auth, so we render it BEFORE
  // the Telegram auth gate — otherwise the SplashScreen would be shown
  // forever for anyone visiting /admin outside Telegram.
  const isAdminRoute = location.pathname.startsWith('/admin');
  // The demo / marketing-capture route doesn't need Telegram auth either
  // — it renders pre-defined cards purely for screen recording. Lives
  // outside the main app so users won't stumble in by accident.
  const isDemoRoute = location.pathname.startsWith('/demo');

  // Tell Telegram to paint its header / bottom bar / window background
  // in our app's night-purple instead of the default white. Without
  // this, screenshots and the actual user experience have an awkward
  // white strip above and below our content. Safe no-op if WebApp API
  // isn't available (e.g. opened in plain browser at /admin).
  useEffect(() => {
    if (isAdminRoute || isDemoRoute) return;
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return;
    try {
      tg.ready?.();
      tg.expand?.();
      // Our brand background gradient runs from #1e1740 (top) to
      // #0b0820 (bottom). We paint the header with the lighter shade
      // so it blends with the top of the page, and the bottom bar +
      // window background with the darker shade.
      tg.setHeaderColor?.('#1e1740');
      tg.setBackgroundColor?.('#0b0820');
      tg.setBottomBarColor?.('#0b0820');
    } catch {
      // Older Telegram clients may not implement all setters — ignore.
    }
  }, [isAdminRoute]);

  // Hijack the system Back button (Android hardware/gesture back, iOS
  // top-bar back) so it navigates inside our app instead of closing
  // the WebApp. Without this, Android users instinctively hit the
  // system back and get kicked out to the chat — confusing UX.
  //
  // The button is shown on every screen EXCEPT Home (`/`). On Home,
  // the system back has its default behaviour: closes the WebApp,
  // which is correct ("I'm done with the app").
  //
  // SPLIT into TWO effects to avoid a timing bug:
  //   • Registering the handler in the same effect that flips show/hide
  //     on every path change caused the FIRST navigation after app load
  //     to miss the handler (Telegram had not finished initialising the
  //     BackButton internals yet). After a manual app reload it worked
  //     because everything was already warmed up.
  //   • Now the handler is registered ONCE on mount, and a separate
  //     effect only toggles show/hide on path change. The handler reads
  //     the CURRENT path at click time via a ref, so it always navigates
  //     from the actual current screen.

  // Always-current path ref — read inside the back handler so we don't
  // capture a stale closure of `location.pathname`.
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  // 1) Register the handler ONCE on mount.
  useEffect(() => {
    if (isAdminRoute || isDemoRoute) return;
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return;

    const handler = () => {
      navigate(parentRoute(pathRef.current));
    };

    try {
      // onEvent is the broader API that works across more Telegram
      // client versions than BackButton.onClick directly.
      tg.onEvent?.('backButtonClicked', handler);
    } catch {}

    return () => {
      try { tg.offEvent?.('backButtonClicked', handler); } catch {}
    };
  }, [isAdminRoute, navigate]);

  // 2) Toggle visibility based on current path.
  useEffect(() => {
    if (isAdminRoute || isDemoRoute) return;
    const backBtn = (window as any).Telegram?.WebApp?.BackButton;
    if (!backBtn) return;

    try {
      if (location.pathname === '/') {
        backBtn.hide?.();
      } else {
        backBtn.show?.();
      }
    } catch {}
  }, [location.pathname, isAdminRoute]);

  useEffect(() => {
    // Skip Telegram auth on the admin route — admin uses its own token.
    if (isAdminRoute || isDemoRoute) return;
    let cancelled = false;
    // Exponential backoff: 800ms → 1.6s → 3.2s → 6.4s. Four attempts is
    // enough to ride out a typical Supabase pool hiccup (1-3 seconds)
    // without keeping the user on the splash for too long if the backend
    // is actually down.
    const MAX_ATTEMPTS = 4;

    (async () => {
      let lastErr: Error | null = null;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (cancelled) return;
        try {
          if (i > 0) {
            const delayMs = 800 * Math.pow(2, i - 1);
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => setTimeout(r, delayMs));
            if (cancelled) return;
          }
          // eslint-disable-next-line no-await-in-loop
          const { user: u, needs_onboarding } = await authWithTelegram();
          if (cancelled) return;
          setUser(u);
          setNeedsOnboarding(needs_onboarding);
          setAuthError(null);

          const storedLang = localStorage.getItem('i18nextLng');
          if (u.language && !storedLang) {
            const backendLang = u.language.startsWith('ru') ? 'ru' : 'en';
            i18n.changeLanguage(backendLang);
            try { localStorage.setItem('i18nextLng', backendLang); } catch {}
          }

          const refId = parseReferrerId(startParam);
          if (refId) {
            try { localStorage.setItem('referrer_id', String(refId)); } catch {}
          }

          const inviteId = parseCompatInvite(startParam);
          if (inviteId) navigate(`/compat/${inviteId}`, { replace: true });
          return;  // success — bail out of the retry loop
        } catch (err) {
          lastErr = err as Error;
          console.warn(`auth attempt ${i + 1}/${MAX_ATTEMPTS} failed`, err);
        }
      }
      if (cancelled) return;
      // All retries exhausted — surface a friendly retry UI rather than
      // letting the splash screen hang forever.
      setAuthError(lastErr?.message ?? 'unknown error');
    })();
    return () => { cancelled = true; };
    // authAttempt is in the deps so the manual "Try again" button can
    // re-trigger the effect by bumping it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAttempt]);

  // Admin route — bypass Telegram auth entirely.
  if (isAdminRoute) {
    return (
      <>
        <Starfield />
        <Routes>
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </>
    );
  }

  // Demo / marketing-capture route — also bypasses Telegram auth. No
  // Starfield overlay here because the demo page paints its own clean
  // background optimised for screen recording.
  if (isDemoRoute) {
    return (
      <Routes>
        <Route path="/demo" element={<Demo />} />
      </Routes>
    );
  }

  return (
    <>
      <Starfield />

      {user ? (
        <>
          <Header />
          <ScrollToTop />
          <Routes>
            <Route
              path="/"
              element={needsOnboarding ? <Navigate to="/onboarding" replace /> : <Home />}
            />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/spreads" element={<Spreads />} />
            <Route path="/reading/:kind" element={<Reading />} />
            <Route path="/dream" element={<Dream />} />
            <Route path="/yes-no" element={<YesNo />} />
            <Route path="/weekly" element={<Weekly />} />
            <Route path="/name-compat" element={<NameCompat />} />
            <Route path="/numerology" element={<Numerology />} />
            <Route path="/natal-chart" element={<NatalChart />} />
            <Route path="/synastry" element={<SynastryScreen />} />
            <Route path="/angel" element={<AngelNumber />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/themed" element={<Themed />} />
            <Route path="/compat" element={<Compat />} />
            <Route path="/compat/:inviteId" element={<Compat />} />
            <Route path="/master" element={<Master />} />
            <Route path="/rituals" element={<Rituals />} />
            <Route path="/diary" element={<Diary />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </>
      ) : authError ? (
        <AuthErrorScreen
          onRetry={() => {
            setAuthError(null);
            setAuthAttempt((n) => n + 1);
          }}
        />
      ) : (
        <SplashScreen />
      )}
    </>
  );
}


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}


function SplashScreen() {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        className="text-6xl text-gold-400"
      >
        ✦
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="font-serif text-3xl text-gold-300"
      >
        Mystic Companion
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 0.8, duration: 0.8 }}
        className="text-sm text-gold-300/70"
      >
        {t('splash.loading')}
      </motion.p>
    </div>
  );
}


/**
 * Shown when all auth retries have failed. We don't try to recover
 * automatically anymore — the user needs to actively tell us to try
 * again, otherwise we'd loop forever on a genuinely-down backend and
 * burn their battery.
 *
 * The wording is deliberately gentle ("сервер занят" not "ошибка") so
 * a transient hiccup doesn't feel like a crash.
 */
function AuthErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="text-6xl text-gold-400/70"
      >
        ☾
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="font-serif text-2xl text-gold-300"
      >
        {t('splash.error_title')}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.75 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="max-w-xs text-sm leading-relaxed text-gold-300/70"
      >
        {t('splash.error_body')}
      </motion.p>
      <motion.button
        type="button"
        onClick={onRetry}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="rounded-full border border-gold-500/50 bg-night-900/60
                   px-6 py-2.5 font-serif text-sm text-gold-200
                   shadow-[0_0_18px_rgba(232,195,86,0.25)]
                   transition hover:border-gold-400 hover:text-gold-100
                   active:scale-95"
      >
        ↻ {t('splash.error_retry')}
      </motion.button>
    </div>
  );
}
